import { Injectable } from '@angular/core';
import { FormGroup, FormControl, FormArray } from '@angular/forms';
import * as flat from 'flat';

@Injectable({ providedIn: 'root' })
export class PhotoReviewNavigationService {

	arraysToFlat: Array<string>;
	arraysToExclude: Array<string>;

	constructor() {
		this.arraysToFlat = [
			'carStorages',
			'carStorageDeficiencies',
			'carStorageUpdates',
			'structures',
			'exteriorDeficiencies',
			'mechanicalDeficiencies',
			'exteriorUpdates',
			'exteriorFeatures',
			'units',
			'levels',
			'rooms',
			'interiorDeficiencies',
			'interiorUpdates'
		];
		this.arraysToExclude = ['photos'];
		this.nameCallback = this.nameCallback.bind(this);
	}

	/**
   * Callback to execute when the object navigation needs to display an item
   * @param control: FormGroup
   * @param parent: string
   * @param index: string
   */
	nameCallback(control: FormGroup, parent: string, index: number): string {
		const childrenKeyNameMap = {
			carStorages: 'Car Storage',
			carStorageDeficiencies: 'Storage Deficiency',
			carStorageUpdates: 'Storage Update',
			adverseSiteConditions: 'Adverse Site Condition',
			siteImprovements: 'Site Improvement',
			ingroundPoolConcerns: 'Inground Pool Concern',
			nonResidentialUses: 'Non Residential Use',
			waterfrontSiteImprovements: 'Waterfront Site Improvement',
			structures: 'Structure',
			exteriorDeficiencies: 'Exterior Deficiency',
			mechanicalDeficiencies: 'Mechanical Deficiency',
			exteriorUpdates: 'Exterior Update',
			exteriorFeatures: 'Exterior Feature',
			units: 'Unit',
			levels: 'Level',
			rooms: 'Room',
			interiorDeficiencies: 'Interior Deficiency',
			interiorUpdates: 'Interior Update',
		};

		if (Object.prototype.hasOwnProperty.call(childrenKeyNameMap, parent)) {
			return `${childrenKeyNameMap[parent]}${index !== undefined ? ' ' + (index + 1) : ''}`;
		} else if (parent !== undefined) {
			return parent.split(/(?=[A-Z])/).map(s => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
		} else {
			return 'Order';
		}
	}

	/**
   * Given photos, reconstruct the order based on each photo jsonPath
   * @param photos: array
   */
	getOrder(photos: Array<any>): FormGroup {
		const groupedPhotos = this.groupByPath(photos);
		const order = flat.unflatten(groupedPhotos);
		const form = this.objectToForm(order);
		return form;
	}

	/**
   * recursively get the photos of the given object
   * @param photos: array
   */
	getPhotos(element: any): Array<any> {
		let photos = [];
		Object.keys(element).forEach(key => {
			if (element[key].constructor === Array) {
				if (key === 'photos') {
					photos = [...photos, ...element.photos];
				} else {
					element[key].forEach(el => {
						photos = [...photos, ...this.getPhotos(el)];
					});
				}
			}
		});
		return photos;
	}

	/**
   * Get the current attachment file related with the order
   * @param orderId: number
   * @param category: string
   */
	private groupByPath(photos: Array<any>): any {
		return photos.reduce((grouped, current) => {
			const photoJsonPath = current.metadata.photoJsonPath
				.replace(/\]$/g, '').replace(/\[/g, '.').replace(/\]\./g, '.') // replace brackets with dots (jsonpath notation)
				//eslint-disable-next-line
				.replace(/([a-z])\.([a-z])/gi, '$1\.0\.$2').replace(/([a-z])$/, '$1\.0'); // single objects to array of 1 object
			if (!Object.prototype.hasOwnProperty.call(grouped, photoJsonPath)) {
				grouped[photoJsonPath] = { 'photos': [] };
			}
			grouped[photoJsonPath]['photos'].push(current);
			return grouped;
		}, {});
	}

	/**
   * Transform object into a reactiveForm
   * @param obj: any
   */
	private objectToForm(obj: any): FormGroup {
		const result: any = obj.constructor.name !== 'Array' ? new FormGroup({}) : new FormArray([]);
		for (const key in obj) {
			if (obj[key] !== null && typeof obj[key] === 'object') {
				result.setControl(key, this.objectToForm(obj[key]));
			} else {
				result.setControl(key, new FormControl(obj[key]));
			}
		}
		return result;
	}

}
